import { computed, ref, unref, type ComputedRef, type Ref } from 'vue';
import { toast } from 'vue-sonner';
import type { Client, ClientQuickLink } from '~/types/models';

type MaybeRef<T> = Ref<T> | ComputedRef<T>;

export function useClientWorkspaceQuickLinks(options: {
    client: MaybeRef<Client | null>;
    dialogOpen: Ref<boolean>;
    clientStore: any;
    refreshClient: () => Promise<void>;
}) {
    const drafts = ref<ClientQuickLink[]>([]);
    const saving = ref(false);

    function createDraft(input?: Partial<ClientQuickLink>): ClientQuickLink {
        const color = String(input?.color || '').trim();
        return {
            id: String(input?.id || `quick_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`),
            label: String(input?.label || '').trim(),
            url: String(input?.url || '').trim(),
            color: /^#[0-9a-f]{6}$/i.test(color) ? color : '#0f766e',
        };
    }

    function normalize(links: ClientQuickLink[]): ClientQuickLink[] {
        return links.map(createDraft).filter(link => link.label && link.url);
    }

    const links = computed(() => normalize(unref(options.client)?.quickLinks || []));

    function open() {
        drafts.value = links.value.length ? links.value.map(createDraft) : [createDraft()];
        options.dialogOpen.value = true;
    }

    function add() {
        drafts.value = [...drafts.value, createDraft()];
    }

    function remove(id: string) {
        drafts.value = drafts.value.filter(link => link.id !== id);
        if (!drafts.value.length) add();
    }

    async function save() {
        const client = unref(options.client);
        if (!client?.id) return;
        saving.value = true;
        try {
            await options.clientStore.updateClient(client.id, { quickLinks: normalize(drafts.value) });
            await options.refreshClient();
            options.dialogOpen.value = false;
            toast.success('Client quick links saved.');
        } catch (error: any) {
            toast.error(error?.message || 'Unable to save quick links.');
        } finally {
            saving.value = false;
        }
    }

    return { drafts, saving, links, open, add, remove, save };
}
